import React from 'react';
import _ from 'lodash';
import Moment from 'react-moment';
import { useHistory } from 'react-router-dom';
import { ReactComponent as HelpIcon } from '../../../../assets/icons/help-circle.svg';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/file-download.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-unfilled.svg';
import { ReactComponent as DollarIcon } from '../../../../assets/icons/dollar.svg';
import Parser from 'html-react-parser';
import l from '../../../../lang';
import './styles.scss';
import Loading from '../../../common/loading';
import MessageError from '../../../common/message-error';
import { createDate } from '../../../../utils/index';
import Popup from '../../../common/popup';
import ButtonCustom from '../../../common/__deprecated__/button-custom';
import SegmentedProgressBar from '../../../common/segmented-progress-bar';
import DialogExportTransactions from '../../transactions/export/dialog';
import { useTransactions } from '../../../../store/transactions';
import Card from '../../../common/card';
import { Routes } from '../../../../config/routes';
import CardTitle from '../../../common/card-title';

const Balances = ({ balances }) => {
  const { showExportTransactions } = useTransactions();
  const history = useHistory();

  const formatCurrency = value => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  const getTotalBalanceForBar = ({ availableBalance = 0, inTransitBalance = 0, withdrawingBalance = 0 } = {}) =>
    availableBalance + inTransitBalance + withdrawingBalance;

  const getSegments = data => {
    const availableBalance = data?.availableBalance || 0;
    const inTransitBalance = data?.inTransitBalance || 0;
    const withdrawingBalance = data?.withdrawingBalance || 0;

    return [
      {
        label: l('home.balance.availableBalance'),
        value: availableBalance,
        color: '#21A35B',
      },
      {
        label: l('home.balance.inTransitBalance'),
        value: inTransitBalance,
        color: '#EABA1F',
      },
      {
        label: l('home.balance.withdrawing'),
        value: withdrawingBalance,
        color: '#006CFA',
      },
    ];
  };

  return (
    <>
      <DialogExportTransactions />
      <Card>
        <div className="balances">
          <div className="balances__header">
            <div className="balances__header-left">
              <CardTitle>{l('home.balances.title')}</CardTitle>
              <p className="products-desc">{l('home.balances.subtitle')}</p>
            </div>
            {!balances.loading && !balances.error && !_.isEmpty(balances.data) && (
              <div className="balances__header-right">
                <ButtonCustom
                  text={<span className="balances__download-text">{l('home.balances.downloadBalance')}</span>}
                  onClick={() => {
                    history.push(Routes.TRANSACTIONS_STATEMENT);
                  }}
                  variant="outlined"
                  color="primary"
                  startIcon={<DownloadIcon className="balances__download-icon" />}
                />
              </div>
            )}
          </div>

          {balances.loading && (
            <div className="balances__loading">
              <Loading />
            </div>
          )}

          {balances.error && (
            <div className="balances__error">
              <MessageError />
            </div>
          )}

          {!balances.loading && !balances.error && _.isEmpty(balances.data) && (
            <div className="balances__no-balance">
              <div>{l('balances.noData')}</div>
            </div>
          )}

          {!balances.loading && !balances.error && !_.isEmpty(balances.data) && (
            <div className="balances__content">
              <div className="balances__main-card">
                <div className="balances__main-card-header">
                  <h2 className="balances__main-card-title">
                    {l('home.balance.currentBalance')} {balances.data.currency}
                  </h2>
                  <div className="balances__main-card-total">
                    {balances.data.currency} {formatCurrency(balances.data.currentBalance)}
                  </div>
                </div>

                <div className="balances__progress-section">
                  <SegmentedProgressBar
                    segments={getSegments(balances.data)}
                    total={getTotalBalanceForBar(balances.data)}
                    showLegend={false}
                    className="balances__progress-bar"
                  />

                  <div className="balances__labels">
                    <div className="balances__label-item">
                      <span className="balances__label-text">{l('home.balance.availableBalance')}</span>
                      <Popup
                        text={l('balance.availableBalance.desc')}
                        icon={<HelpIcon className="balances__help-icon balances__help-icon--green" />}
                      />
                    </div>
                    <div className="balances__label-item">
                      <span className="balances__label-text">{l('home.balance.inTransitBalance')}</span>
                      <Popup
                        text={l('balance.inTransitBalance.desc')}
                        icon={<HelpIcon className="balances__help-icon balances__help-icon--yellow" />}
                      />
                    </div>
                    <div className="balances__label-item">
                      <span className="balances__label-text">{l('home.balance.withdrawing')}</span>
                      <Popup
                        text={l('balance.withdrawing.desc')}
                        icon={<HelpIcon className="balances__help-icon balances__help-icon--blue" />}
                      />
                    </div>
                  </div>

                  <div className="balances__legend">
                    <div className="balances__legend-item">
                      <div className="balances__legend-dot" style={{ backgroundColor: '#21A35B' }} />
                      <span className="balances__legend-value">{formatCurrency(balances.data.availableBalance)}</span>
                    </div>
                    <div className="balances__legend-item">
                      <div className="balances__legend-dot" style={{ backgroundColor: '#EABA1F' }} />
                      <span className="balances__legend-value">{formatCurrency(balances.data.inTransitBalance)}</span>
                    </div>
                    <div className="balances__legend-item">
                      <div className="balances__legend-dot" style={{ backgroundColor: '#006CFA' }} />
                      <span className="balances__legend-value">{formatCurrency(balances.data.withdrawingBalance || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="balances__credits-card">
                <div className="balances__credits-info">
                  <Popup
                    text={
                      <div className="balances__credits-tooltip">
                        <h3 className="balances__credits-tooltip-title">{l('home.balances.credits')}</h3>
                        <p className="balances__credits-tooltip-desc">{Parser(l('balance.credits.desc'))}</p>
                        <div className="balances__credits-tooltip-button">
                          <ButtonCustom
                            text={l('balance.credits.button')}
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              history.push(Routes.TRANSACTIONS_AFFILIATIONS);
                            }}
                          />
                        </div>
                      </div>
                    }
                    icon={<InfoIcon className="balances__credits-info-icon" />}
                  />
                </div>
                <div className="balances__credits-icon-wrapper">
                  <DollarIcon className="balances__credits-icon" />
                </div>
                <h3 className="balances__credits-title">{l('home.balances.credits')}</h3>
                <div className="balances__credits-value">
                  {balances.data?.currency} {formatCurrency(balances.data?.availableCredits || 0)}
                </div>
              </div>
            </div>
          )}

          {!balances.loading && !balances.error && !_.isEmpty(balances.data) && balances.data.updatedAt && (
            <div className="balances__last-update">
              <span>{l('home.balance.lastUpdate')} </span>
              <Moment format="DD/MM/YYYY hh:mm:ss a">{createDate(balances.data.updatedAt)}</Moment>
            </div>
          )}
        </div>
      </Card>
    </>
  );
};

export default Balances;
